import React, {useContext, useEffect, useState} from "react";
import {Box, Text} from "grommet";
import {useRouter} from "next/router";
import {formatDate} from "../../../utils/dates";
import {handleErrors} from "../../../utils/errors";
import {formatCurrency} from "../../../utils/numbers";
import Drawer from "../../Drawer";
import {NotificationsContext} from "../../NotificationsProvider";
import CustomerTag from "../../CustomerTag";
import InvoiceTag from "../../InvoiceTag";
import FontAwesomeIcon from "../../FontAwesomeIcon";
import { getTxnTypeDisplayDetails } from "../../../utils/transactions";
import StatusMessage from "../../StatusMessage";
import MultipleInvoiceTags from "../../MultipleInvoiceTags";
import {
    faChevronLeft as IconPrevious,
    faChevronRight as IconNext,
} from '@fortawesome/pro-light-svg-icons';
import { EnhancedButton } from "../../../utils/styled";

const PendingTransactionDetails = ({
    transactionId,
    pendingTransactionType,
    transactionIds: propTransactionIds = [],
    onClose
}) => {
    const router = useRouter();
    const [data, setData] = useState();
    const [transactionIds, setTransactionIds] = useState(propTransactionIds);
    const notificationCtx = useContext(NotificationsContext);

    // Load transaction IDs from sessionStorage if not provided via props
    useEffect(() => {
        if (propTransactionIds.length === 0) {
            const storedIds = sessionStorage.getItem('pendingTransactionIds');
            if (storedIds) {
                try {
                    const parsedIds = JSON.parse(storedIds);
                    setTransactionIds(parsedIds);
                } catch (e) {
                    console.error('Failed to parse pending transaction IDs from sessionStorage', e);
                }
            }
        } else {
            setTransactionIds(propTransactionIds);
        }
    }, [propTransactionIds]);

    // Find current transaction index in the list
    const currentIndex = transactionIds.findIndex(txn => String(txn.id) === String(transactionId));
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex >= 0 && currentIndex < transactionIds.length - 1;

    // Navigation handlers
    const handlePrevious = () => {
        if (hasPrevious) {
            const prevTxn = transactionIds[currentIndex - 1];
            router.push(`/transactions?action=view&pendingTransactionId=${prevTxn.id}&pendingTransactionType=${prevTxn.type}`, undefined, { scroll: false });
        }
    }

    const handleNext = () => {
        if (hasNext) {
            const nextTxn = transactionIds[currentIndex + 1];
            router.push(`/transactions?action=view&pendingTransactionId=${nextTxn.id}&pendingTransactionType=${nextTxn.type}`, undefined, { scroll: false });
        }
    }

    useEffect(() => {
        fetch(`/api/transactions/pending/${transactionId}?pending_transaction_type=${pendingTransactionType}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then(handleErrors)
            .then((data) => setData(data))
            .catch(e => {
                router.back();
                notificationCtx.showNotification({type: 'error', message: 'No transaction details available.'});
            });
    }, [transactionId, pendingTransactionType]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ignore if user is typing in an input/textarea
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            switch(e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                if (hasPrevious) {
                    e.preventDefault();
                    handlePrevious();
                }
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                if (hasNext) {
                    e.preventDefault();
                    handleNext();
                }
                break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasPrevious, hasNext, transactionIds, currentIndex]);

    const txnType = getTxnTypeDisplayDetails(data);
    const Line = ({label, value, wrap}) => (
        <Box justify="between" direction="row" align={wrap ? "start" : "center"} height={wrap ? undefined : "30px"} gap="small">
            <Text>{label}</Text>
            <Box align="end">
                <Text textAlign="end">{value}</Text>
            </Box>
        </Box>
    );

    const previousTransaction = () => (
        <EnhancedButton
            ctaBorderColor={'border'}
            onClick={handlePrevious}
            disabled={!hasPrevious}
            styles={`
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            `}
            label={<FontAwesomeIcon SelectedIcon={IconPrevious} size="xsmall" />} />
    )

    const nextTransaction = () => (
        <EnhancedButton
            ctaBorderColor={'border'}
            onClick={handleNext}
            disabled={!hasNext}
            styles={`
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
                margin-left: -1px;
            `}
            label={<FontAwesomeIcon SelectedIcon={IconNext} size="xsmall" />} />
    )

    return data ? (
        <Drawer
            onClose={onClose}
            leftControl={
                <Box direction="row">
                    {previousTransaction()}
                    {nextTransaction()}
                </Box>
            }
            title={txnType.name}
        >
            <Box pad={{horizontal: 'small'}} flex={false} gap="small">
                <Box direction="row" margin={{vertical: 'small'}} gap="small" align="center">
                    <FontAwesomeIcon SelectedIcon={txnType.icon} color="text" size="large" highlight="accent" />
                </Box>
                <StatusMessage
                    message={
                        <Box pad={{left: 'xsmall'}}>
                            <Text weight={600} size='small'>This payment is still processing</Text>
                            <Text size="small">It should be available by {formatDate(data?.estimated_settle_date)}</Text>
                        </Box>
                    }
                    condensed
                    margin={{top: '0'}}
                    type="info" />
                <Box gap="small" direction="column" pad={{bottom: 'small'}}>
                    <Box gap="xsmall" direction="column" pad={{bottom: 'small'}}>
                        <Line label="Amount" value={formatCurrency(data?.amount, data?.currency)} />
                        <Line label="Fee" value={formatCurrency(data?.fee, data?.currency)} />
                        <Line label="Net Amount" value={formatCurrency(data?.amount - data?.fee, data?.currency)} />
                    </Box>
                    {data?.recipient_uuid && (<Line label="Customer" value={(<CustomerTag uuid={data?.recipient_uuid}/>)} inline />)}
                    <Line
                        label="Invoice"
                        wrap
                        value={
                            (data?.linked_invoice_ids && data?.linked_invoice_ids.length > 0) ? (
                                <Box pad={{vertical: 'xsmall'}}>
                                    {data?.linked_invoice_ids.length > 1 ? (
                                        <MultipleInvoiceTags ids={data?.linked_invoice_ids} inline />
                                    ) : (
                                        <InvoiceTag id={data?.linked_invoice_ids[0]} />
                                    )}
                                </Box>
                            ) : data?.linked_invoice_id ? (
                                <Box pad={{vertical: 'xsmall'}}>
                                    <InvoiceTag id={data?.linked_invoice_id} />
                                </Box>
                            ) : (
                                // A pending payment usually has no invoice yet, so
                                // this is the ordinary case rather than missing
                                // data. Rendering null left the label with nothing
                                // beside it, which reads as a value that failed to
                                // load. Same dash the transactions list uses for an
                                // absent value.
                                <Text color="text-weak">-</Text>
                            )
                        }
                    />
                    {data.created_at && (<Line label="Payment made on" value={formatDate(data?.created_at)} />)}
                </Box>
            </Box>
        </Drawer>
    ) : (<></>);
}



export default PendingTransactionDetails;
